import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useEffect, useRef, useState } from 'react';

interface ProgressLoaderProps {
    isLoading: boolean;
    estimatedTime: number; // estimated time in seconds
    onFinished: () => void;
    infoMessage?: string;
}

const COMPLETION_DELAY_MS = 20;
const UPDATE_INTERVAL_MS = 300;
const FINISH_DELAY_MS = 500;
const INITIAL_MAX_PERCENTAGE = 99; // loader will stop at 99 until operation is complete
const COMPLETION_INCREMENT = 5; // step size for complete animation

export const ProgressLoader: React.FC<ProgressLoaderProps> = ({
    isLoading,
    estimatedTime,
    onFinished,
    infoMessage,
}) => {
    const [progress, setProgress] = useState(0);
    const setCumulativeProgress = useState(0)[1];
    const progressInterval = useRef<any | undefined>(undefined);
    const completionInterval = useRef<any | undefined>(undefined);

    const calculateProgressIncrement = () => {
        const totalSteps = (estimatedTime * 1000) / UPDATE_INTERVAL_MS;
        return INITIAL_MAX_PERCENTAGE / totalSteps;
    };

    const animateToComplete = () => {
        clearInterval(progressInterval.current);
        // Complete animation if operation is completed faster than estimated time
        completionInterval.current = setInterval(() => {
            setProgress((prev) => {
                const newProgress = Math.round(Math.min(prev + COMPLETION_INCREMENT, 100));
                if (newProgress >= 100) {
                    clearInterval(completionInterval.current);
                    setTimeout(onFinished, FINISH_DELAY_MS);
                }
                return newProgress;
            });
        }, COMPLETION_DELAY_MS);
    };

    useEffect(() => {
        if (isLoading) {
            setProgress(0);
            setCumulativeProgress(0);
            const progressIncrement = calculateProgressIncrement();
            progressInterval.current = setInterval(() => {
                setCumulativeProgress((prev) => {
                    const newCumulative = Math.min(prev + progressIncrement, INITIAL_MAX_PERCENTAGE);
                    setProgress(Math.round(newCumulative));
                    return newCumulative;
                });
            }, UPDATE_INTERVAL_MS);
        } else if (progressInterval.current) {
            animateToComplete();
        }

        return () => {
            if (progressInterval.current) clearInterval(progressInterval.current);
            if (completionInterval.current) clearInterval(completionInterval.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, estimatedTime]);

    if (!isLoading && progress === 0) return null;

    return (
        <Column spacing={'300'} alignmentHorizontal={'center'} data-testid={'progress-loader'}>
            <Row spacing={'400'} alignmentHorizontal={'center'}>
                <Loader
                    size={'large'}
                    percentage={progress}
                    showPercentageText={true}
                    data-testid={'progress-loader-bar'}
                />
            </Row>
            {infoMessage && (
                <Row spacing={'400'} alignmentHorizontal={'center'}>
                    <Text type={'b300'} data-testid={'progress-loader-message'}>
                        {infoMessage}
                    </Text>
                </Row>
            )}
        </Column>
    );
};
